/**
 * blobErrorBody.ts — тело отказа выгрузки, пришедшее не JSON-объектом
 * (WEB-2469, кабинет; решение оркестратора О-6).
 *
 * ЗАЧЕМ. Выгрузки кабинет качает с `responseType: 'blob'` (журнал команды,
 * журнал списаний, оборвавшиеся), поэтому тело отказа — JSON
 * `{ statusCode, code, message }` глобального фильтра сервера — приходит
 * `Blob`'ом. Перехватчик `client.ts` читает `error.response.data.code`: у
 * `Blob` его нет, и `localizeError` отдавал человеку запасное «Не удалось…»
 * вместо фразы кода («не удалось записать в журнал…»). Выгрузка лидов идёт
 * `fetch` мимо клиента (`LeadsTab.tsx`) и печатала «Экспорт не удался (503)».
 *
 * Здесь — только разбор; подключение — одна строка в перехватчике
 * (`client.ts`) и одна в `LeadsTab.tsx`. `Blob` в jsdom без `.text()`, поэтому
 * читаем через `FileReader` (он есть и в браузере, и в jsdom).
 */

/** Тело больше этого — не отказ, а файл: не читаем. */
const MAX_ERROR_BODY_BYTES = 64 * 1024;

type RefusalError = Error & { status?: number; code?: string };

function isBlob(value: unknown): value is Blob {
  return typeof Blob !== 'undefined' && value instanceof Blob;
}

function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** JSON-объект из текста; не объект или не JSON — `undefined`. */
function parseObject(text: string): Record<string, unknown> | undefined {
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Тело ответа как есть, а если это `Blob` с JSON-объектом внутри — разобранный
 * объект. Никогда не бросает: не прочиталось, не JSON, слишком большое —
 * возвращается исходное значение (перехватчик сработает как раньше).
 */
export async function readBlobErrorBody(data: unknown): Promise<unknown> {
  if (!isBlob(data) || data.size > MAX_ERROR_BODY_BYTES) return data;
  try {
    return parseObject(await blobText(data)) ?? data;
  } catch {
    return data;
  }
}

/**
 * Отказ той же формы, что даёт перехватчик `client.ts`: `Error` с текстом
 * сервера, `.status` и `.code` — `localizeError` читает код первым.
 *
 * Кода нет и текста нет — запасной текст вызывающего. Код есть, а текста нет —
 * текст ПУСТОЙ, чтобы решил код: русский запасной текст в русском кабинете
 * перебил бы фразу реестра (`localizeError` показывает кириллицу раньше неё),
 * а перехватчик в том же случае кладёт английское «Request failed…», которое
 * коду не мешает.
 */
export function refusalFromBody(body: unknown, status: number, fallback: string): RefusalError {
  const b = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const raw = Array.isArray(b.message) ? b.message[0] : b.message;
  const code = typeof b.code === 'string' && b.code ? b.code : undefined;
  const text = typeof raw === 'string' && raw ? raw : code ? '' : fallback;
  const err: RefusalError = new Error(text);
  err.status = status;
  if (code) err.code = code;
  return err;
}

/** Отказ ответа `fetch` (выгрузка лидов): тело разбирается, сбой чтения — прежний текст. */
export async function refusalFromResponse(
  res: { status: number; text: () => Promise<string> },
  fallback: string,
): Promise<RefusalError> {
  let body: unknown;
  try {
    body = parseObject(await res.text());
  } catch {
    body = undefined;
  }
  return refusalFromBody(body, res.status, fallback);
}
